import { Component, computed, effect, HostListener, inject, OnInit, output, signal } from '@angular/core';
import {
  IconButtonComponent,
  ImgComponent,
  KeyValueLabel,
  SelectSignalComponent,
  toImageUri,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { isActive, Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PfAngularOauth2OidcService } from '@bnpp-pf/pf-angular-oauth2-oidc';
import { AutoLogOutService } from '../../authentication/autologout.service';
import { UserService } from '../../user-connection/user.service';
import { AppConfig } from '../../../app.config';
import { firstValueFrom } from 'rxjs';
import { MenuEnum } from './nav-bar.model';
import { ApplicationStore } from '../../../core/services/application-store.service';
import { isAdmin } from '../../helpers/user-right.helper';
import { form, FormField } from '@angular/forms/signals';

@Component({
  selector: 'pvmo-nav-bar',
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  imports: [RouterModule, IconButtonComponent, ImgComponent, TranslatePipe, SelectSignalComponent, FormField],
  templateUrl: './nav-bar.component.html',
  styleUrl: './nav-bar.component.scss'
})
export class NavBarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(PfAngularOauth2OidcService);
  private readonly autoLogOutService = inject(AutoLogOutService);
  private readonly config = inject(AppConfig);
  private readonly translateService = inject(TranslateService);
  private readonly applicationStore = inject(ApplicationStore);

  opacity = output<boolean>();
  toggledMenu = false;
  menuMobile: KeyValueLabel[] = [];
  readonly navBarModel = signal<{ select: KeyValueLabel | null }>({ select: null });
  readonly navBarForm = form(this.navBarModel);
  pingLogoutUrl = this.config.oidcConfig?.revokeUrl
    ? `${this.config.oidcConfig?.revokeUrl}?TargetResource=${this.config.oidcConfig?.redirectUri}`
    : null;
  isUserAdmin = computed(() => isAdmin(this.applicationStore.instant('userData')()?.userRights));
  isHomeActive = isActive('/home', this.router, {
    paths: 'exact',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  isSimulationActive = isActive('/simulation', this.router, {
    paths: 'subset',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  isRecordsActive = isActive('/records', this.router, {
    paths: 'exact',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  isLeadsActive = isActive('/leads', this.router, {
    paths: 'exact',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  isParametersActive = isActive('/parameters', this.router, {
    paths: 'exact',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  isAdministrationActive = isActive('/administration', this.router, {
    paths: 'exact',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  });

  protected readonly userService = inject(UserService);

  protected readonly src = signal<string | null>('');
  protected readonly brandImageName = signal<string>('');

  constructor() {
    effect(() => {
      const theming = this.applicationStore.instant('logoData')();
      this.src.set(
        theming?.logoBytes && theming?.logoFileName ? toImageUri(theming.logoFileName, theming.logoBytes) : null
      );
      this.brandImageName.set([theming?.brand, 'logo'].join(' '));
    });
    effect(() => {
      const menu = this.navBarForm.select().value();
      switch (menu?.key.toLowerCase() as MenuEnum) {
        case MenuEnum.HOME:
          this.homeClicked();
          break;
        case MenuEnum.SIMULATION:
          this.simulationClicked();
          break;
        case MenuEnum.RECORDS:
          this.recordsClicked();
          break;
        case MenuEnum.LEADS:
          this.leadsClicked();
          break;
        case MenuEnum.PARAMETERS:
          this.parametersClick();
          break;
        case MenuEnum.LOGOUT:
          this.logout();
          break;
        case MenuEnum.ADMINISTRATION:
          this.administrationClicked();
          break;
      }
    });
  }

  ngOnInit(): void {
    firstValueFrom(this.translateService.use(this.translateService.getCurrentLang() ?? 'fr')).then(() => {
      const keys = Object.keys(MenuEnum);
      for (const key of keys) {
        this.menuMobile.push({
          key: key,
          value: key,
          label: this.translateService.instant(`application.nav.${key.toLowerCase()}`)
        });
      }
      return this.menuMobile;
    });
  }

  homeClicked(): void {
    void this.router.navigateByUrl('/home');
  }

  simulationClicked(): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigateByUrl('/simulation');
    });
    this.applicationStore.update('leadsFilters', null);
  }

  recordsClicked(): void {
    void this.router.navigateByUrl('/records');
  }

  leadsClicked(): void {
    void this.router.navigateByUrl('/leads');
  }

  parametersClick(): void {
    void this.router.navigateByUrl('/parameters');
  }

  administrationClicked(): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigateByUrl('/administration');
    });
  }

  logout(): void {
    this.autoLogOutService.stopMonitoring();
    if (this.pingLogoutUrl) {
      window.location.href = this.pingLogoutUrl;
    } else {
      this.authService.logout();
    }
  }

  @HostListener('mouseover')
  onMouseOver(): void {
    this.toggledMenu = true;
    this.opacity.emit(true);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.toggledMenu = false;
    this.opacity.emit(false);
  }
}
